import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  PaperPlaneTilt,
  CaretDown,
  LinkSimple,
  ChatText,
  WarningCircle,
} from "@phosphor-icons/react";
import { useSendPaymentSummarySmsMutation } from "../../store/services/financeApi";
import { setClinicProfile } from "../../store/slices/authSlice";
import {
  DEFAULT_PAYMENT_SUMMARY_SMS,
  upgradeLegacyPaymentSummaryText,
} from "../../firebase";
import { useToast } from "../../contexts/ToastContext";
import "./PaymentSummarySmsCard.css";

const MAX_CHARS = 200;
const MAX_SELECTED = 10;

// Default message text per mode. Used to prefill when switching mode (only
// when the current text is empty or still equals another preset, so custom
// text is never clobbered) — same pattern as the end-meeting SMS settings.
const PS_PRESETS = {
  template: DEFAULT_PAYMENT_SUMMARY_SMS.messageText,
  template_link:
    'היי {name}, סה"כ {amount} ש"ח לתשלום עבור הטיפולים בתאריכים: {dates}. לתשלום נוח: {link}',
};
const PS_PRESET_VALUES = [PS_PRESETS.template, PS_PRESETS.template_link];

const MODE_TABS = [
  { id: "template", label: "הודעה מובנית" },
  { id: "template_link", label: "הודעה עם קישור" },
];

// Transaction dates arrive Redux-serialized (ISO string / Date / Timestamp).
const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDayMonth = (date) =>
  date.toLocaleDateString("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
  });

const formatFullDate = (date) =>
  date
    ? date.toLocaleDateString("he-IL", {
        timeZone: "Asia/Jerusalem",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

const formatWeekday = (date) =>
  date
    ? date.toLocaleDateString("he-IL", {
        timeZone: "Asia/Jerusalem",
        weekday: "long",
      })
    : "";

const formatTimeOfDay = (date) =>
  date
    ? date.toLocaleTimeString("he-IL", {
        timeZone: "Asia/Jerusalem",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const formatAmount = (amount) =>
  `₪${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;

/**
 * Payment summary SMS card — patient profile finance tab.
 * The therapist picks the pending payments to include and sends a manual SMS
 * (templated / templated with payment link, max 200 chars).
 * Template + link are clinic-shared: persisted on the clinic doc by the
 * Cloud Function. Server enforces a 15-minute per-(therapist, patient)
 * cooldown and the monthly SMS quota.
 *
 * Error Code: CK-3610
 */
const PaymentSummarySmsCard = ({
  clinicId,
  patientId,
  patient,
  pendingTransactions,
  appointments = [],
  therapistId,
  therapistName,
  clinicProfile,
}) => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const [sendPaymentSummarySms, { isLoading: isSending }] =
    useSendPaymentSummarySmsMutation();

  const [expanded, setExpanded] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionInitialized, setSelectionInitialized] = useState(false);
  const [mode, setMode] = useState("template");
  const [messageText, setMessageText] = useState(PS_PRESETS.template);
  const [linkUrl, setLinkUrl] = useState("");
  const [sentRecently, setSentRecently] = useState(false);

  // Appointment lookup for meeting day/time on the selection rows. Mirrors the
  // server's date chain: appointment startTime -> expectedPaymentDate -> createdAt.
  const appointmentById = useMemo(() => {
    const map = new Map();
    (appointments || []).forEach((apt) => map.set(apt.id, apt));
    return map;
  }, [appointments]);

  // Load the clinic-shared config once from the clinic profile.
  useEffect(() => {
    const cfg = clinicProfile?.paymentSummarySms;
    if (cfg) {
      const type = MODE_TABS.some((t) => t.id === cfg.type)
        ? cfg.type
        : "template";
      setMode(type);
      if (cfg.messageText) {
        // A stored superseded default means the clinic never customised the
        // text — show the current default instead of the old wording.
        setMessageText(upgradeLegacyPaymentSummaryText(cfg.messageText));
      }
      setLinkUrl(cfg.linkUrl || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transactions load async: preselect all (up to the cap) once when they
  // first arrive, then only prune selections for payments that were settled.
  useEffect(() => {
    if (!selectionInitialized && pendingTransactions.length > 0) {
      setSelectedIds(
        pendingTransactions.slice(0, MAX_SELECTED).map((tx) => tx.id)
      );
      setSelectionInitialized(true);
      return;
    }
    setSelectedIds((prev) =>
      prev.filter((id) => pendingTransactions.some((tx) => tx.id === id))
    );
  }, [pendingTransactions, selectionInitialized]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setMessageText((prev) => {
      const isPreset = !prev || PS_PRESET_VALUES.includes(prev.trim());
      return isPreset ? PS_PRESETS[nextMode] : prev;
    });
  };

  const toggleTransaction = (txId) => {
    setSelectedIds((prev) => {
      if (prev.includes(txId)) return prev.filter((id) => id !== txId);
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, txId];
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.length > 0
        ? []
        : pendingTransactions.slice(0, MAX_SELECTED).map((tx) => tx.id)
    );
  };

  const selectedTransactions = useMemo(
    () => pendingTransactions.filter((tx) => selectedIds.includes(tx.id)),
    [pendingTransactions, selectedIds]
  );

  const totalAmount = useMemo(
    () =>
      selectedTransactions.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0),
    [selectedTransactions]
  );

  // Display dates: expectedPaymentDate -> createdAt. The server recomputes
  // dates authoritatively (prefers the linked appointment's startTime).
  const dateStrings = useMemo(() => {
    const dates = selectedTransactions
      .map((tx) => toDateSafe(tx.expectedPaymentDate) || toDateSafe(tx.createdAt))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
    return [...new Set(dates.map(formatDayMonth))];
  }, [selectedTransactions]);

  const amountText = Number.isInteger(totalAmount)
    ? String(totalAmount)
    : totalAmount.toFixed(2);
  const datesText = dateStrings.join(", ");
  const patientFirstName = patient?.firstName || "";
  const patientPhone = patient?.phone || patient?.mobilePhone || "";

  // Live preview with tokens resolved against the real selection, so the
  // length check reflects the actual SMS.
  const preview = (messageText || "")
    .replace(/\{name\}/g, patientFirstName)
    .replace(/\{amount\}/g, amountText)
    .replace(/\{dates\}/g, datesText)
    .replace(/\{link\}/g, mode === "template_link" ? linkUrl || "https://..." : "");

  const charCount = preview.length;
  const tooLong = charCount > MAX_CHARS;
  const missingLink = mode === "template_link" && !linkUrl.trim();
  const emptyMessage = !messageText.trim();

  const sendDisabled =
    isSending ||
    sentRecently ||
    selectedIds.length === 0 ||
    tooLong ||
    missingLink ||
    emptyMessage ||
    !patientPhone;

  const handleSend = async () => {
    if (sendDisabled) return;

    const config = {
      type: mode,
      messageText: messageText.trim(),
      linkUrl: linkUrl.trim(),
    };

    try {
      const result = await sendPaymentSummarySms({
        clinicId,
        patientId,
        transactionIds: selectedIds,
        mode,
        freeText: null,
        config,
        therapistId,
        therapistName,
        patientName: `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim(),
      }).unwrap();

      if (result) {
        showSuccess("הודעת סיכום התשלומים נשלחה למטופל.");
        setSentRecently(true);
        // Keep the clinic-shared template in Redux so other patients see it
        // without a refetch (the Cloud Function persisted it on the clinic doc).
        dispatch(
          setClinicProfile({ ...clinicProfile, paymentSummarySms: config })
        );
      }
    } catch (error) {
      console.error("[CK-3610] Error sending payment summary SMS:", {
        errorCode: "CK-3610",
        errorMessage: error?.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שגיאה בשליחת הודעת סיכום התשלומים");
    }
  };

  if (pendingTransactions.length === 0) return null;

  return (
    <div className={`pssms-card ${expanded ? "expanded" : ""}`}>
      <button
        type="button"
        className="pssms-header"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="pssms-header-icon">
          <PaperPlaneTilt size={16} weight="fill" />
        </span>
        <span className="pssms-header-text">
          <span className="pssms-title">שליחת סיכום תשלומים ב-SMS</span>
          <span className="pssms-subtitle">
            {pendingTransactions.length} תשלומים ממתינים
          </span>
        </span>
        <CaretDown size={16} className="pssms-caret" />
      </button>

      {expanded && (
        <div className="pssms-body">
          {/* Transaction selection */}
          <div className="pssms-section">
            <div className="pssms-section-header">
              <span className="pssms-section-title">בחר/י תשלומים לכלול</span>
              <button type="button" className="pssms-select-all" onClick={toggleAll}>
                {selectedIds.length > 0 ? "נקה בחירה" : "בחר/י הכל"}
              </button>
            </div>
            <div className="pssms-tx-list">
              {pendingTransactions.map((tx) => {
                const apt = tx.appointmentId
                  ? appointmentById.get(tx.appointmentId)
                  : null;
                const meetingDate = apt?.startTime
                  ? toDateSafe(apt.startTime)
                  : null;
                const txDate =
                  meetingDate ||
                  toDateSafe(tx.expectedPaymentDate) ||
                  toDateSafe(tx.createdAt);
                // Manual incomes carry a date-only timestamp — hide "00:00".
                const timeText = formatTimeOfDay(txDate);
                const showTime = Boolean(meetingDate) || timeText !== "00:00";
                const checked = selectedIds.includes(tx.id);
                const atCap = !checked && selectedIds.length >= MAX_SELECTED;
                return (
                  <label
                    key={tx.id}
                    className={`pssms-tx-row ${checked ? "checked" : ""} ${
                      atCap ? "disabled" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={atCap}
                      onChange={() => toggleTransaction(tx.id)}
                    />
                    <span className="pssms-tx-info">
                      <span className="pssms-tx-date">
                        {formatFullDate(txDate)}
                      </span>
                      <span className="pssms-tx-meta">
                        {formatWeekday(txDate)}
                        {showTime ? ` · ${timeText}` : ""}
                      </span>
                    </span>
                    <span className="pssms-tx-amount">
                      {formatAmount(Number(tx.amount) || 0)}
                    </span>
                  </label>
                );
              })}
            </div>
            {pendingTransactions.length > MAX_SELECTED && (
              <div className="pssms-hint">ניתן לבחור עד {MAX_SELECTED} תשלומים</div>
            )}
            <div className="pssms-selection-summary">
              נבחרו {selectedIds.length} תשלומים · סה"כ {formatAmount(totalAmount)}
              {datesText ? ` · תאריכים: ${datesText}` : ""}
            </div>
          </div>

          {/* Mode tabs */}
          <div className="pssms-tabs" role="tablist">
            {MODE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={mode === tab.id}
                className={`pssms-tab ${mode === tab.id ? "active" : ""}`}
                onClick={() => changeMode(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Message editor */}
          <div className="pssms-section">
            <textarea
              className="pssms-textarea"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={3}
              dir="rtl"
            />
            <div className="pssms-placeholders">
              <span className="pssms-chip">{"{name}"} שם המטופל</span>
              <span className="pssms-chip">{"{amount}"} סכום</span>
              <span className="pssms-chip">{"{dates}"} תאריכים</span>
              {mode === "template_link" && (
                <span className="pssms-chip">{"{link}"} קישור</span>
              )}
            </div>
            {mode === "template_link" && (
              <div className="pssms-link-field">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="קישור לתשלום (למשל Bit / PayBox)"
                />
                <LinkSimple size={16} />
              </div>
            )}
          </div>

          {/* Preview + counter */}
          <div className="pssms-section">
            <div className="pssms-preview-header">
              <span className="pssms-section-title">
                <ChatText size={14} /> תצוגה מקדימה
              </span>
              <span className={`pssms-counter ${tooLong ? "over" : ""}`}>
                {charCount}/{MAX_CHARS}
              </span>
            </div>
            <div className="pssms-preview" dir="rtl">
              {preview || "ההודעה תוצג כאן..."}
            </div>
            {tooLong && (
              <div className="pssms-error">
                <WarningCircle size={14} /> ההודעה חורגת מ-{MAX_CHARS} תווים
              </div>
            )}
            {!patientPhone && (
              <div className="pssms-error">
                <WarningCircle size={14} /> לא נמצא מספר טלפון למטופל
              </div>
            )}
          </div>

          <button
            type="button"
            className="pssms-send-btn"
            onClick={handleSend}
            disabled={sendDisabled}
          >
            <PaperPlaneTilt size={16} weight="fill" />
            {isSending
              ? "שולח..."
              : sentRecently
              ? "נשלח. ניתן לשלוח שוב בעוד כ-15 דקות"
              : "שליחת SMS למטופל"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentSummarySmsCard;
